import type {PublicationContext, Subscription} from 'centrifuge';
import {SubscriptionState} from 'centrifuge';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PosterCollaboration} from '@PosterWhiteboard/collaboration/poster-collaboration.class';
import type {PosterCollaborationConnection} from '@PosterWhiteboard/collaboration/poster-collaboration-connection.class';
import type {CanvasPoint, PresenceMessage, PresencePayload, PresenceUpdateMessage} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';
import type {ClientPoint} from '@PosterWhiteboard/collaboration/presence-pointer.library';
import {distanceFromPointToRect, toCanvasPointOnPage} from '@PosterWhiteboard/collaboration/presence-pointer.library';
import {
  PRESENCE_HEARTBEAT_INTERVAL_MS,
  PRESENCE_PUBLISH_INTERVAL_MS,
  PresenceMessageType,
  REMOTE_SESSION_PRUNE_INTERVAL_MS,
} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {isCollaborationParticipant, isPresenceMessage} from '@PosterWhiteboard/collaboration/presence-sessions.library';

interface PointerOnPage {
  pageId: string;
  point: CanvasPoint;
}

/**
 * Tells the other tabs editing this poster where this tab is, and keeps the Redux mirror of
 * where they are. Every tab publishes its page, pointer and selection on the poster's presence
 * channel whenever they change while someone else is here, and a heartbeat always; the overlay and the nav-bar roster render
 * what comes back. The pointer is followed over the whole whiteboard, not just the pages, and
 * travels in the canvas coordinates of the nearest page, so each viewer projects it through its
 * own zoom and still sees a cursor resting in the margin around a page.
 *
 * Joined and left by PosterCollaboration, which owns the connection.
 */
export class PosterPresence {
  #connection: PosterCollaborationConnection | null = null;
  #subscription: Subscription | null = null;
  #publishTimer: ReturnType<typeof setInterval> | null = null;
  #pruneTimer: ReturnType<typeof setInterval> | null = null;
  #pointer: ClientPoint | null = null;
  #lastPublishedState = '';
  #lastPublishedAt = 0;
  readonly #onPointerMove = (event: PointerEvent): void => {
    this.#pointer = {clientX: event.clientX, clientY: event.clientY};
  };
  readonly #onPointerLeave = (): void => {
    this.#pointer = null;
  };
  readonly #onPageHide = (): void => {
    void this.leave();
  };

  constructor(
    private readonly poster: Poster,
    private readonly collaboration: PosterCollaboration
  ) {}

  public join(connection: PosterCollaborationConnection): void {
    this.#connection = connection;
    this.#subscription = connection.subscribe('presence', this.#onPublication.bind(this));
    this.#startTracking();
  }

  /** Tells the others this tab is gone and unsubscribes once that message is out; resolves when the channel is left. */
  public leave(): Promise<void> {
    this.#stopTracking();
    this.poster.redux.resetPosterPresence();
    return this.#leaveChannel();
  }

  #startTracking(): void {
    const surface = this.#getPointerSurface();
    surface.addEventListener('pointermove', this.#onPointerMove);
    surface.addEventListener('pointerleave', this.#onPointerLeave);
    window.addEventListener('pagehide', this.#onPageHide);
    this.#publishTimer = setInterval(this.#publishWhenChanged.bind(this), PRESENCE_PUBLISH_INTERVAL_MS);
    this.#pruneTimer = setInterval((): void => {
      this.poster.redux.pruneStaleRemoteSessions(Date.now());
    }, REMOTE_SESSION_PRUNE_INTERVAL_MS);
  }

  #stopTracking(): void {
    const surface = this.#getPointerSurface();
    surface.removeEventListener('pointermove', this.#onPointerMove);
    surface.removeEventListener('pointerleave', this.#onPointerLeave);
    window.removeEventListener('pagehide', this.#onPageHide);
    if (this.#publishTimer !== null) {
      clearInterval(this.#publishTimer);
      this.#publishTimer = null;
    }
    if (this.#pruneTimer !== null) {
      clearInterval(this.#pruneTimer);
      this.#pruneTimer = null;
    }
    this.#pointer = null;
    this.#lastPublishedState = '';
    this.#lastPublishedAt = 0;
  }

  /** Publishes the leave message before unsubscribing: unsubscribing first would discard it. */
  async #leaveChannel(): Promise<void> {
    const connection = this.#connection;
    const subscription = this.#subscription;
    if (connection === null || subscription === null) {
      return;
    }
    this.#connection = null;
    this.#subscription = null;
    if (subscription.state === SubscriptionState.Subscribed) {
      await subscription.publish({type: PresenceMessageType.LEAVE, sessionId: this.collaboration.sessionId} satisfies PresenceMessage).catch(ignoreLostLeaveMessage);
    }
    connection.unsubscribe(subscription);
  }

  /**
   * Alone in the poster, only the heartbeat goes out: nobody would see the cursor move, and the
   * heartbeat is what lets a newcomer find this tab. Once someone else is here, every change goes out.
   */
  #publishWhenChanged(): void {
    const message: PresenceUpdateMessage = {type: PresenceMessageType.UPDATE, ...this.#buildPayload(), editSeq: this.#getEditSeq()};
    const state = JSON.stringify(message);
    const now = Date.now();
    const isHeartbeatDue = now - this.#lastPublishedAt >= PRESENCE_HEARTBEAT_INTERVAL_MS;
    const isChangeWorthPublishing = state !== this.#lastPublishedState && this.collaboration.isSharedWithOthers();
    if (!isHeartbeatDue && !isChangeWorthPublishing) {
      return;
    }
    this.#publish(message);
    this.#lastPublishedState = state;
    this.#lastPublishedAt = now;
  }

  #getEditSeq(): number | null {
    return this.collaboration.liveEdits.isActive() ? this.collaboration.liveEdits.getLastAppliedSeq() : null;
  }

  #buildPayload(): PresencePayload {
    const pointerOnPage = this.#getPointerOnPage();
    const pageId = pointerOnPage?.pageId ?? this.poster.getCurrentPageId();
    return {
      sessionId: this.collaboration.sessionId,
      pageId,
      cursor: pointerOnPage?.point ?? null,
      selectedItemIds: this.poster.pages.pagesHashMap[pageId]?.getSelectedItems().map((item) => item.uid) ?? [],
    };
  }

  /** The whole scrollable editor area around the pages; the poster's own element when the editor shell is not mounted (headless, tests). */
  #getPointerSurface(): HTMLElement {
    return window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER]?.get(0) ?? this.poster.HTMLElement;
  }

  /** The page nearest the pointer (the one under it when there is one) and the pointer's position in that page's canvas pixels. */
  #getPointerOnPage(): PointerOnPage | null {
    const pointer = this.#pointer;
    if (pointer === null) {
      return null;
    }
    let nearest: {pageId: string; rect: DOMRect; distance: number} | null = null;
    for (const page of Object.values(this.poster.pages.pagesHashMap)) {
      const rect = page.canvasBackdrop.getBoundingClientRect();
      if (rect.width === 0) {
        continue;
      }
      const distance = distanceFromPointToRect(pointer, rect);
      if (nearest === null || distance < nearest.distance) {
        nearest = {pageId: page.hashedID, rect, distance};
      }
    }
    if (nearest === null) {
      return null;
    }
    return {pageId: nearest.pageId, point: toCanvasPointOnPage(pointer, nearest.rect, this.poster)};
  }

  #publish(message: PresenceMessage): void {
    if (this.#subscription?.state !== SubscriptionState.Subscribed) {
      return;
    }
    void this.#subscription.publish(message).catch(this.#republishOnNextTick.bind(this));
  }

  /** A publish lost mid-reconnect is not an error worth surfacing: the next tick sends the current state again. */
  #republishOnNextTick(): void {
    this.#lastPublishedState = '';
    this.#lastPublishedAt = 0;
  }

  #onPublication(context: PublicationContext): void {
    const message: unknown = context.data;
    if (!isPresenceMessage(message) || message.sessionId === this.collaboration.sessionId) {
      return;
    }
    const participant: unknown = context.info?.chanInfo;
    if (!isCollaborationParticipant(participant)) {
      return;
    }
    if (message.type === PresenceMessageType.LEAVE) {
      this.poster.redux.remoteSessionLeft({sessionId: message.sessionId, participant});
      this.collaboration.liveEdits.catchUpAfterPeerLeft();
      return;
    }
    const {type: _type, editSeq, ...payload} = message;
    const sessionCountBefore = this.poster.redux.getRemoteSessionCount();
    this.poster.redux.remoteSessionSeen({payload, participant, now: Date.now()});
    if (this.poster.redux.getRemoteSessionCount() > sessionCountBefore) {
      this.#greetNewcomerOnNextTick();
    }
    if (typeof editSeq === 'number') {
      this.collaboration.liveEdits.catchUpIfStillBehind(editSeq);
    }
    this.collaboration.onRemoteSessionsChanged();
  }

  /** A newcomer learns about this tab from its next publication, so it goes out on the next tick instead of at the next heartbeat. */
  #greetNewcomerOnNextTick(): void {
    this.#lastPublishedAt = 0;
  }
}

const ignoreLostLeaveMessage = (): void => {
  // The others drop a silent session after the stale timeout anyway.
};
